// frontend/src/modules/esign/components/RecipientsEditor.tsx
//
// Step 2 of the prepare wizard: who signs (or is cc'd), in what order.
// Pure controlled component — the wizard page owns the recipients array and
// signing order; this just renders rows + an optional "pick from team"
// affordance. Role changes and removals are routed back to the parent via
// `onRoleChangeRequest`/`onRemoveRequest` because dropping a recipient who
// already has placed fields needs a confirm dialog + field cleanup, which
// this component has no visibility into (it only knows about recipients).
import * as React from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, Users } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import type { EsignRecipientRole, EsignSigningOrder } from '../types';

// A plain native <select> (not the Radix-based design-system Select) —
// deliberate: this dropdown is exercised heavily in jsdom unit tests
// (role-switch-to-CC gating fields), and a native element is trivial and
// reliable to drive with fireEvent/userEvent there, unlike a portal-based
// Radix listbox. Styled to match the Input control's look.
const nativeSelectClass =
  'flex h-11 items-center rounded-2xl bg-white/80 dark:bg-white/[0.04] backdrop-blur-md px-4 py-2 ' +
  'text-[13.5px] text-ink dark:text-white shadow-pill dark:shadow-none ring-1 ring-ink/8 dark:ring-white/10 ' +
  'font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40 ' +
  'transition-all duration-150 disabled:cursor-not-allowed disabled:opacity-50';

export interface DraftRecipient {
  /** Client-generated, stable for the lifetime of the row in this wizard session. */
  localId: string;
  /** Real recipient id — set once `saveRecipients` has persisted this row. */
  id: string | null;
  role: EsignRecipientRole;
  name: string;
  email: string;
  employee_id: string | null;
}

export interface PickableEmployee {
  id: string;
  name: string;
  email: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function makeLocalId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `local-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

export function isRecipientRowValid(r: DraftRecipient): boolean {
  return r.name.trim().length > 0 && EMAIL_RE.test(r.email.trim());
}

export function recipientsAreValid(recipients: DraftRecipient[]): boolean {
  return (
    recipients.length > 0 &&
    recipients.some((r) => r.role === 'signer') &&
    recipients.every(isRecipientRowValid)
  );
}

interface RecipientsEditorProps {
  recipients: DraftRecipient[];
  onRecipientsChange: (next: DraftRecipient[]) => void;
  signingOrder: EsignSigningOrder;
  onSigningOrderChange: (order: EsignSigningOrder) => void;
  onRoleChangeRequest: (localId: string, nextRole: EsignRecipientRole) => void;
  onRemoveRequest: (localId: string) => void;
  employees: PickableEmployee[] | null;
  /** True while a save is in flight. Disables in-place edits (name/email
   *  inputs, reorder, remove) so the row a save request has already
   *  captured can't be mutated mid-flight. Adding a *new* recipient (or
   *  changing role/signing-order) stays enabled — those don't corrupt the
   *  in-flight snapshot's zip-by-index (see PrepareWizardPage.persistRecipients,
   *  which zips server ids back by localId, not array position). */
  disabled?: boolean;
}

export function RecipientsEditor({
  recipients,
  onRecipientsChange,
  signingOrder,
  onSigningOrderChange,
  onRoleChangeRequest,
  onRemoveRequest,
  employees,
  disabled = false,
}: RecipientsEditorProps) {
  function updateRow(localId: string, patch: Partial<DraftRecipient>) {
    onRecipientsChange(recipients.map((r) => (r.localId === localId ? { ...r, ...patch } : r)));
  }

  function addRow() {
    onRecipientsChange([
      ...recipients,
      { localId: makeLocalId(), id: null, role: 'signer', name: '', email: '', employee_id: null },
    ]);
  }

  function addFromEmployee(emp: PickableEmployee) {
    onRecipientsChange([
      ...recipients,
      { localId: makeLocalId(), id: null, role: 'signer', name: emp.name, email: emp.email, employee_id: emp.id },
    ]);
  }

  function move(index: number, dir: -1 | 1) {
    const next = [...recipients];
    const swapWith = index + dir;
    if (swapWith < 0 || swapWith >= next.length) return;
    [next[index], next[swapWith]] = [next[swapWith], next[index]];
    onRecipientsChange(next);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-6">
        <span className="text-[13px] font-semibold text-ink/70 dark:text-white/70">Signing order</span>
        <label className="flex items-center gap-2 text-[13.5px] cursor-pointer">
          <input
            type="radio"
            name="signing-order"
            checked={signingOrder === 'parallel'}
            onChange={() => onSigningOrderChange('parallel')}
          />
          Anyone, any order (parallel)
        </label>
        <label className="flex items-center gap-2 text-[13.5px] cursor-pointer">
          <input
            type="radio"
            name="signing-order"
            checked={signingOrder === 'sequential'}
            onChange={() => onSigningOrderChange('sequential')}
          />
          In this order (sequential)
        </label>
      </div>

      <div className="space-y-3">
        {recipients.map((r, index) => (
          <div
            key={r.localId}
            className="flex items-center gap-2 rounded-2xl bg-white/70 dark:bg-white/[0.04] ring-1 ring-ink/8 dark:ring-white/10 p-3"
          >
            {signingOrder === 'sequential' && (
              <div className="flex flex-col shrink-0">
                <button
                  type="button"
                  aria-label={`Move ${r.name || 'recipient'} up`}
                  disabled={disabled || index === 0}
                  onClick={() => move(index, -1)}
                  className="disabled:opacity-30"
                >
                  <ChevronUp className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  aria-label={`Move ${r.name || 'recipient'} down`}
                  disabled={disabled || index === recipients.length - 1}
                  onClick={() => move(index, 1)}
                  className="disabled:opacity-30"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
              </div>
            )}

            <Input
              placeholder="Name"
              value={r.name}
              onChange={(e) => updateRow(r.localId, { name: e.target.value })}
              className="flex-1"
              aria-label="Recipient name"
              disabled={disabled}
            />
            <Input
              placeholder="Email"
              type="email"
              value={r.email}
              onChange={(e) => updateRow(r.localId, { email: e.target.value })}
              className="flex-1"
              aria-label="Recipient email"
              disabled={disabled}
            />

            <select
              value={r.role}
              aria-label="Recipient role"
              className={cn(nativeSelectClass, 'w-[180px]')}
              onChange={(e) => onRoleChangeRequest(r.localId, e.target.value as EsignRecipientRole)}
            >
              <option value="signer">Signer</option>
              <option value="cc">CC — view only</option>
            </select>

            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={`Remove ${r.name || 'recipient'}`}
              disabled={disabled}
              onClick={() => onRemoveRequest(r.localId)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={addRow}>
          <Plus className="h-3.5 w-3.5" />
          Add recipient
        </Button>

        {employees && employees.length > 0 && (
          <div className="flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5 text-ink/50 dark:text-white/50" />
            <select
              aria-label="Pick from team"
              className={cn(nativeSelectClass, 'w-[220px]')}
              value=""
              onChange={(e) => {
                const emp = employees.find((x) => x.id === e.target.value);
                if (emp) addFromEmployee(emp);
                e.target.value = '';
              }}
            >
              <option value="" disabled>Pick from team</option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>{e.name} — {e.email}</option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );
}
